import { ApplicationInsights } from '@microsoft/applicationinsights-web';

/**
 * Application Insights instance (singleton)
 */
let appInsightsInstance: ApplicationInsights | null = null;

/**
 * Third parties that reject the Request-Id / traceparent headers App Insights
 * adds when enableCorsCorrelation is true. Patterns match the JS SDK
 * (`.` escaped, `*` → `.*`, unanchored host test).
 */
export const CORRELATION_HEADER_EXCLUDED_DOMAINS = [
  '*.mapbox.com',
  'mapbox.com',
  'api.mapbox.com',
  'events.mapbox.com',
  '*.tiles.mapbox.com',
  'mn-tz.com',
  '*.mn-tz.com',
  '*.mouseflow.com',
  'cdn.mouseflow.com',
  '*.clarity.ms',
  'clarity.ms',
  '*.google.com',
  '*.googleapis.com',
  '*.gstatic.com',
  '*.google-analytics.com',
  '*.googletagmanager.com',
  '*.analytics.google.com',
  '*.doubleclick.net',
  '*.googleadservices.com',
  '*.unsplash.com',
  '*.blob.core.windows.net',
  '*.logrocket.io',
  '*.lgrckt-in.com',
  '*.lr-in.com',
  '*.lr-ingest.io',
  'js.monitor.azure.com',
  '*.applicationinsights.azure.com',
  'dc.applicationinsights.azure.com',
  'dc.services.visualstudio.com',
  '*.ciamlogin.com',
  'login.microsoftonline.com',
  '*.stripe.com',
  'neeed.directory',
  'wired.business',
  'fazier.com',
  'thesaasdir.com',
  'startupfa.me',
  'nicklaunches.com',
  '*.instantlaunch.xyz',
  'twelve.tools',
  'saasbison.com',
  'web-review.com',
  'dailypings.com',
];

/** Hosts that should keep correlation headers (our own API). */
export function buildCorrelationHeaderDomains(input?: {
  pageHostname?: string;
  apiBaseUrl?: string;
}): string[] {
  const domains = new Set<string>([
    'citysage.ch',
    '*.citysage.ch',
    'localhost',
    '127.0.0.1',
  ]);

  const pageHostname = input?.pageHostname?.trim();
  if (pageHostname) {
    domains.add(pageHostname);
  }

  const apiBaseUrl = input?.apiBaseUrl?.trim();
  if (apiBaseUrl) {
    try {
      const hostname = new URL(apiBaseUrl).hostname;
      if (hostname) {
        domains.add(hostname);
      }
    } catch {
      // Ignore malformed API base URLs; the page host still correlates.
    }
  }

  return Array.from(domains);
}

/**
 * Host match used by Application Insights JS
 * (`correlationIdCanIncludeCorrelationHeader`).
 */
export function correlationHeaderMatchesDomain(
  host: string,
  patterns: string[]
): boolean {
  const requestHost = host.toLowerCase();
  return patterns.some((domain) => {
    const source = domain
      .toLowerCase()
      .replace(/\\/g, '\\\\')
      .replace(/\./g, '\\.')
      .replace(/\*/g, '.*');
    return new RegExp(source).test(requestHost);
  });
}

export function shouldAttachCorrelationHeader(
  host: string,
  config: {
    correlationHeaderDomains?: string[];
    correlationHeaderExcludedDomains?: string[];
  }
): boolean {
  const included = config.correlationHeaderDomains;
  if (
    included &&
    included.length > 0 &&
    !correlationHeaderMatchesDomain(host, included)
  ) {
    return false;
  }

  const excluded = config.correlationHeaderExcludedDomains;
  if (
    excluded &&
    excluded.length > 0 &&
    correlationHeaderMatchesDomain(host, excluded)
  ) {
    return false;
  }

  return !!host;
}

/**
 * Create Application Insights configuration
 */
export function createAppInsightsConfig(
  connectionString: string | undefined,
  router?: any
) {
  if (!connectionString) return null;

  const pageHostname =
    typeof window !== 'undefined' ? window.location.hostname : undefined;
  const apiBaseUrl = import.meta.env?.VITE_API_BASE_URL as string | undefined;

  return {
    connectionString,
    enableAutoRouteTracking: true,
    enableCorsCorrelation: true,
    // Correlate only our API. Third-party fetches (Mapbox tiles, analytics)
    // reject Request-Id in their CORS preflight and fail closed.
    correlationHeaderDomains: buildCorrelationHeaderDomains({
      pageHostname,
      apiBaseUrl,
    }),
    correlationHeaderExcludedDomains: CORRELATION_HEADER_EXCLUDED_DOMAINS,
    enableRequestHeaderTracking: true,
    enableResponseHeaderTracking: true,
    enableAjaxErrorStatusText: true,
    enableAjaxPerfTracking: true,
    enableUnhandledPromiseRejectionTracking: true,
    disableFetchTracking: false,
    disableAjaxTracking: false,
    disableExceptionTracking: false,
    disableTelemetry: false,
    loggingLevelConsole: import.meta.env?.MODE === 'development' ? 2 : 0, // 2 = WARNING and above, 0 = CRITICAL only
    loggingLevelTelemetry: 1, // 1 = CRITICAL and ERROR
    maxBatchSizeInBytes: 10000,
    maxBatchInterval: 15000,
    disableDataLossAnalysis: false,
    enableDebug: import.meta.env?.MODE === 'development',
    enablePerfMgr: true,
    perfEvtsSendAll: false,
  };
}

/**
 * Get or create Application Insights instance
 */
export function getAppInsights(): ApplicationInsights | null {
  return appInsightsInstance;
}

const APP_INSIGHTS_COOKIE_NAMES = ['ai_user', 'ai_session'] as const;

/** Remove diagnostic cookies. Used when analytics consent is absent or withdrawn. */
export function clearAppInsightsCookies(): void {
  if (typeof document === 'undefined') return;
  for (const name of APP_INSIGHTS_COOKIE_NAMES) {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;SameSite=Lax`;
  }
}

/**
 * Start Application Insights only after analytics consent.
 * Before that, the SDK is not loaded, so ai_user and ai_session are not set.
 */
export function applyAppInsightsConsent(
  enabled: boolean,
  connectionString: string | undefined
): void {
  if (!enabled) {
    if (appInsightsInstance) {
      const config = appInsightsInstance.config as {
        disableCookiesUsage?: boolean;
        disableTelemetry?: boolean;
      };
      config.disableCookiesUsage = true;
      config.disableTelemetry = true;
    }
    clearAppInsightsCookies();
    return;
  }

  initializeAppInsights(connectionString);
}

/**
 * Initialize Application Insights
 */
export function initializeAppInsights(
  connectionString: string | undefined,
  router?: any
): ApplicationInsights | null {
  if (!connectionString) {
    console.warn('Application Insights connection string not provided');
    return null;
  }

  if (appInsightsInstance) {
    console.warn('Application Insights already initialized');
    return appInsightsInstance;
  }

  try {
    const config = createAppInsightsConfig(connectionString, router);
    if (!config) return null;

    appInsightsInstance = new ApplicationInsights({
      config,
    });

    appInsightsInstance.loadAppInsights();

    // Track initial page view
    appInsightsInstance.trackPageView();

    console.log('Application Insights initialized successfully');
    return appInsightsInstance;
  } catch (error) {
    console.error('Failed to initialize Application Insights:', error);
    return null;
  }
}

/**
 * Track a custom event
 */
export function trackEvent(name: string, properties?: { [key: string]: any }) {
  if (appInsightsInstance) {
    appInsightsInstance.trackEvent({ name }, properties);
  }
}

/**
 * Track an exception
 */
export function trackException(
  error: Error,
  properties?: { [key: string]: any }
) {
  if (appInsightsInstance) {
    appInsightsInstance.trackException({ exception: error }, properties);
  }
}

/**
 * Track a page view
 */
export function trackPageView(name?: string, uri?: string) {
  if (appInsightsInstance) {
    appInsightsInstance.trackPageView({ name, uri });
  }
}

/**
 * Track a metric
 */
export function trackMetric(
  name: string,
  average: number,
  properties?: { [key: string]: any }
) {
  if (appInsightsInstance) {
    appInsightsInstance.trackMetric({ name, average }, properties);
  }
}

/**
 * Track a trace/log message
 */
export function trackTrace(
  message: string,
  properties?: { [key: string]: any }
) {
  if (appInsightsInstance) {
    appInsightsInstance.trackTrace({ message }, properties);
  }
}

/**
 * Set authenticated user context
 */
export function setAuthenticatedUserContext(
  userId: string,
  accountId?: string
) {
  if (appInsightsInstance) {
    appInsightsInstance.setAuthenticatedUserContext(userId, accountId);
  }
}

/**
 * Clear authenticated user context
 */
export function clearAuthenticatedUserContext() {
  if (appInsightsInstance) {
    appInsightsInstance.clearAuthenticatedUserContext();
  }
}

/**
 * Flush telemetry (send all queued telemetry immediately)
 */
export function flushTelemetry() {
  if (appInsightsInstance) {
    appInsightsInstance.flush();
  }
}
